'use client';

import { useState, useEffect, useRef, ReactNode } from 'react';

interface FilterSectionProps {
  children: ReactNode;
  className?: string;
  activeFilterCount?: number;
  stickyPadding?: string;
}

export default function FilterSection({
  children,
  className = '',
  activeFilterCount = 0,
  stickyPadding = 'px-8 md:px-20',
}: FilterSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const originalTopRef = useRef<number | null>(null);
  const sectionHeightRef = useRef<number>(0);

  useEffect(() => {
    const section = sectionRef.current;
    const placeholder = placeholderRef.current;
    if (!section || !placeholder) return;

    // Calculate and store section height
    const measureHeight = () => {
      if (section && !isSticky) {
        sectionHeightRef.current = section.offsetHeight;
      }
    };

    // Calculate original position on mount
    const updateOriginalTop = () => {
      if (placeholder && !isSticky) {
        originalTopRef.current = placeholder.getBoundingClientRect().top + window.scrollY;
        measureHeight();
      }
    };

    updateOriginalTop();

    const handleScroll = () => {
      if (originalTopRef.current === null) return;

      const scrollY = window.scrollY;
      const shouldBeSticky = scrollY > originalTopRef.current;

      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateOriginalTop);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateOriginalTop);
    };
  }, [isSticky]);

  // Reset original top when sticky state changes
  useEffect(() => {
    if (!isSticky && placeholderRef.current && sectionRef.current) {
      // Small delay to let the DOM settle
      requestAnimationFrame(() => {
        if (placeholderRef.current) {
          originalTopRef.current = placeholderRef.current.getBoundingClientRect().top + window.scrollY;
        }
        if (sectionRef.current) {
          sectionHeightRef.current = sectionRef.current.offsetHeight;
        }
      });
    }
  }, [isSticky]);

  return (
    <>
      {/* Placeholder to maintain layout when sticky */}
      <div
        ref={placeholderRef}
        style={{ height: isSticky ? sectionHeightRef.current : 0 }}
        className="transition-[height] duration-0"
      />

      <div
        ref={sectionRef}
        className={`${className} ${
          isSticky
            ? 'fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm shadow-lg shadow-black/20'
            : ''
        }`}
      >
        <div className={`${isSticky ? `max-w-[1440px] mx-auto ${stickyPadding}` : ''}`}>
          {/* Mobile Filter Toggle */}
          <div className="md:hidden flex items-center justify-between py-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm text-[#888888] hover:text-[#F5F5F0] transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <span>篩選</span>
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold">
                  {activeFilterCount}
                </span>
              )}
              <svg
                className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Filter Content */}
          <div
            className={`
              md:block
              overflow-hidden transition-all duration-300 ease-in-out
              ${isExpanded ? 'max-h-[500px] opacity-100 pb-4' : 'max-h-0 opacity-0 md:max-h-none md:opacity-100'}
              ${isSticky ? 'md:py-4' : ''}
            `}
          >
            {children}
          </div>
        </div>
        {/* Bottom border line */}
        <div className="h-[1px] bg-[#D4AF37]/25" />
      </div>
    </>
  );
}
